import React, {
  CSSProperties,
  FC,
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import classNames from "classnames";
import Portal from "../Portal";
import { CloseIcon } from "../Icon";
import {
  ShellSafePopupContainerResolver,
  withShellSafePopupContainer,
} from "../../layout/components/mobileCenteredShell/portal";
import "./index.less";

type DrawerPlacement = "right" | "bottom" | "left" | "top";

export interface DrawerUIProps extends PropsWithChildren {
  autoFocus?: boolean;
  bodyStyle?: CSSProperties;
  className?: string;
  closable?: boolean;
  disabled?: boolean;
  drawerStyle?: CSSProperties;
  focusLock?: boolean;
  footer?: ReactNode;
  footerStyle?: CSSProperties;
  getPopupContainer?: ShellSafePopupContainerResolver;
  headerStyle?: CSSProperties;
  height?: number | string;
  mask?: boolean;
  maskClosable?: boolean;
  maskStyle?: CSSProperties;
  onCancel?: (event?: MouseEvent | KeyboardEvent) => void;
  onClose?: (event?: MouseEvent | KeyboardEvent) => void;
  onOk?: (event?: MouseEvent | KeyboardEvent) => void;
  open?: boolean;
  placement?: DrawerPlacement;
  popupContainerFallback?: Element | null;
  style?: CSSProperties;
  title?: ReactNode;
  unmountOnExit?: boolean;
  visible?: boolean;
  width?: number | string;
  wrapClassName?: string;
  zIndex?: number;
}

const DEFAULT_PLACEMENT: DrawerPlacement = "right";
const DEFAULT_DRAWER_SIZE = 332;

const toSizeValue = (value?: number | string) => {
  if (typeof value === "number") {
    return `${value}px`;
  }

  return value;
};

const DrawerUI: FC<DrawerUIProps> = (props) => {
  const {
    autoFocus = true,
    bodyStyle,
    children,
    className,
    closable = true,
    disabled = false,
    drawerStyle,
    footer,
    footerStyle,
    getPopupContainer,
    headerStyle,
    height,
    mask = true,
    maskClosable = true,
    maskStyle,
    onCancel,
    onClose,
    open,
    placement = DEFAULT_PLACEMENT,
    popupContainerFallback,
    style,
    title,
    visible,
    width,
    wrapClassName,
    zIndex,
  } = props;
  const panelRef = useRef<HTMLDivElement>(null);
  const isVisible = visible ?? open ?? false;
  const shouldRender = isVisible;
  const popupContainerResolver = useMemo(
    () => withShellSafePopupContainer(getPopupContainer, popupContainerFallback),
    [getPopupContainer, popupContainerFallback]
  );
  const getContainer = useCallback(
    () => popupContainerResolver(undefined),
    [popupContainerResolver]
  );

  const closeDrawer = useCallback(
    (event?: MouseEvent | KeyboardEvent) => {
      if (disabled) {
        return;
      }

      onCancel?.(event);
      onClose?.(event);
    },
    [disabled, onCancel, onClose]
  );

  useEffect(() => {
    if (!isVisible || !autoFocus) {
      return;
    }

    panelRef.current?.focus();
  }, [autoFocus, isVisible]);

  if (!shouldRender) {
    return null;
  }

  const isHorizontal = placement === "left" || placement === "right";
  const panelSizeStyle = isHorizontal
    ? { width: toSizeValue(width) || `${DEFAULT_DRAWER_SIZE}px` }
    : { height: toSizeValue(height) || `${DEFAULT_DRAWER_SIZE}px` };
  const panelStyle = {
    ...panelSizeStyle,
    ...drawerStyle,
    ...style,
  };

  return (
    <Portal visible getContainer={getContainer}>
      <div
        className={classNames(
          "drawer-ui-wrapper",
          "drawer-ui-wrapper-open",
          wrapClassName
        )}
        style={{ zIndex }}
      >
        {mask && (
          <div
            aria-hidden="true"
            className="drawer-ui-mask"
            style={maskStyle}
            onClick={(event) => {
              if (maskClosable) {
                closeDrawer(event);
              }
            }}
          />
        )}
        <div
          ref={panelRef}
          aria-disabled={disabled || undefined}
          aria-modal="true"
          className={classNames(
            "drawer-ui-panel",
            `drawer-ui-${placement}`,
            {
              "drawer-ui-disabled": disabled,
              "drawer-ui-no-title": title === null || title === undefined,
            },
            className
          )}
          role="dialog"
          style={panelStyle}
          tabIndex={-1}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              closeDrawer(event);
            }
          }}
        >
          {(title !== null && title !== undefined) || closable ? (
            <div className="drawer-ui-header" style={headerStyle}>
              <div className="drawer-ui-title">{title}</div>
              {closable && (
                <button
                  aria-label="Close"
                  className="drawer-ui-close"
                  disabled={disabled}
                  type="button"
                  onClick={closeDrawer}
                >
                  <CloseIcon />
                </button>
              )}
            </div>
          ) : null}
          <div className="drawer-ui-body" style={bodyStyle}>
            {children}
          </div>
          {footer !== null && footer !== undefined ? (
            <div className="drawer-ui-footer" style={footerStyle}>
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </Portal>
  );
};

export default DrawerUI;
